import { BulbOutlined, HistoryOutlined } from '@ant-design/icons';
import {
  IDEA_FREQUENCY_LABELS,
  IDEA_IMPACT_DESCRIPTIONS,
  IDEA_IMPACT_LABELS,
  IDEA_PROBLEM_MAX,
  IDEA_REACH_LABELS,
  IDEA_SOLUTION_MAX,
  type IdeaFrequency,
  type IdeaImpact,
  type IdeaReach,
  REFERENCE_TYPES,
  TITLE_MAX,
} from '@cult/domain';
import type { AttachmentInput, IdeaCreateInput } from '@cult/schemas';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { App as AntApp, Button, Card, Flex, Form, Input, Select, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useEffect, useMemo, useState } from 'react';
import { BackButton, FormPageSkeleton, InlineError, LeavePageModal, PageContainer, PageHeader } from '../../components';
import { englishOnlyRule, plainTypographyOnPaste } from '../../lib/english-only';
import { FIX_HIGHLIGHTED_FIELDS, applyServerFieldErrors } from '../../lib/form-errors';
import { useUnsavedChangesGuard } from '../../lib/use-unsaved-changes-guard';

import { AttachmentsField } from '../../features/attachments';
import { useReportError } from '../../features/client-logs';
import {
  IDEA_PROBLEM_HINT,
  IDEA_PROBLEM_LABEL,
  IDEA_SOLUTION_HINT,
  IDEA_SOLUTION_LABEL,
  IDEA_TITLE_MAX_WORDS,
  IdeaChecklist,
  IdeaGuide,
  IdeaScaleField,
  clearDraft,
  countWords,
  isDraftMeaningful,
  readDraft,
  readWorkplace,
  useCreateIdea,
  writeDraft,
  writeWorkplace,
} from '../../features/ideas';
import { useReferenceItems } from '../../features/reference';

import styles from './submit.module.scss';

dayjs.extend(relativeTime);

// Frequency, reach and impact start unpicked; the required rules make sure they're set before submit.
type FormValues = Omit<IdeaCreateInput, 'frequency' | 'reach' | 'impact'> & {
  frequency?: IdeaFrequency;
  reach?: IdeaReach;
  impact?: IdeaImpact;
};

const EMPTY_FORM: FormValues = {
  title: '',
  problem: '',
  solution: '',
  categoryId: '',
  countryId: '',
  departmentId: '',
  attachments: [],
};

// Shown by default (the form starts with ''), but can't be picked — the required rule treats '' as
// empty, so submit still demands a real value.
const withPlaceholder = (label: string, items: { id: string; name: string }[] | undefined) => [
  { value: '', label, disabled: true },
  ...(items?.map((i) => ({ value: i.id, label: i.name })) ?? []),
];

const SubmitPage = () => {
  const navigate = useNavigate();
  const categoriesQuery = useReferenceItems(REFERENCE_TYPES.CATEGORY);
  useReportError('ideas.submit.categories', categoriesQuery.error);
  const countriesQuery = useReferenceItems(REFERENCE_TYPES.COUNTRY);
  useReportError('ideas.submit.countries', countriesQuery.error);
  const departmentsQuery = useReferenceItems(REFERENCE_TYPES.DEPARTMENT);
  useReportError('ideas.submit.departments', departmentsQuery.error);
  const categoryOptions = withPlaceholder('Select category', categoriesQuery.data);
  const countryOptions = withPlaceholder('Select country', countriesQuery.data);
  const departmentOptions = withPlaceholder('Select department', departmentsQuery.data);
  const createIdea = useCreateIdea();
  const { message } = AntApp.useApp();

  // Read draft once on mount; it drives Form.initialValues and the "restored" bar. Discard drops it, so a
  // remount of the form starts empty instead of bringing the draft back.
  const [restored, setRestored] = useState(() => readDraft());
  // Country and department from the author's last idea. Read once; only ids still on offer are used.
  const workplace = useMemo(() => readWorkplace(), []);
  const lastCountryId = countriesQuery.data?.some((c) => c.id === workplace.countryId) ? workplace.countryId : null;
  const lastDepartmentId = departmentsQuery.data?.some((d) => d.id === workplace.departmentId)
    ? workplace.departmentId
    : null;
  const workplaceFilledIn = lastCountryId !== null || lastDepartmentId !== null;
  const [formKey, setFormKey] = useState(0);
  const [showRestoreBanner, setShowRestoreBanner] = useState(restored !== null && isDraftMeaningful(restored));

  const initialValues: FormValues = {
    ...(restored
      ? {
          title: restored.title,
          problem: restored.problem,
          solution: restored.solution,
          categoryId: restored.categoryId ?? '',
          frequency: restored.frequency ?? undefined,
          reach: restored.reach ?? undefined,
          impact: restored.impact ?? undefined,
          attachments: restored.attachments,
        }
      : EMPTY_FORM),
    countryId: lastCountryId ?? '',
    departmentId: lastDepartmentId ?? '',
  };

  const [form] = Form.useForm<FormValues>();
  const title = Form.useWatch('title', form) ?? '';
  const problem = Form.useWatch('problem', form) ?? '';
  const solution = Form.useWatch('solution', form) ?? '';
  const categoryId = Form.useWatch('categoryId', form) ?? '';
  const frequency = Form.useWatch('frequency', form);
  const reach = Form.useWatch('reach', form);
  const impact = Form.useWatch('impact', form);
  const attachments = Form.useWatch('attachments', form) ?? [];

  const hasChanges =
    title.trim().length > 0 ||
    problem.trim().length > 0 ||
    solution.trim().length > 0 ||
    categoryId.length > 0 ||
    frequency !== undefined ||
    reach !== undefined ||
    impact !== undefined ||
    (Array.isArray(attachments) && attachments.length > 0);

  const { markSubmitted, isSubmitted, leavePageModalProps } = useUnsavedChangesGuard(hasChanges);
  const isDirty = !isSubmitted() && hasChanges;

  // Debounced autosave whenever the form is dirty. We checkpoint at ~400 ms
  // after the last change.
  useEffect(() => {
    if (!isDirty) return;
    const t = window.setTimeout(() => {
      writeDraft({
        title,
        problem,
        solution,
        categoryId: categoryId.length > 0 ? categoryId : null,
        frequency: frequency ?? null,
        reach: reach ?? null,
        impact: impact ?? null,
        attachments: Array.isArray(attachments) ? (attachments as AttachmentInput[]) : [],
      });
    }, 400);
    return () => window.clearTimeout(t);
  }, [isDirty, title, problem, solution, categoryId, frequency, reach, impact, attachments]);

  const onFinish = (values: FormValues) => {
    // The required rules have run, so frequency, reach and impact are set.
    createIdea.mutate(values as IdeaCreateInput, {
      onSuccess: (idea) => {
        markSubmitted();
        clearDraft();
        message.success(`Submitted as #${idea.number}`);
        navigate({ to: '/ideas/$id', params: { id: String(idea.number) } });
      },
      onError: (err) => {
        const marked = applyServerFieldErrors(form, err);
        message.error(marked ? FIX_HIGHLIGHTED_FIELDS : `Couldn't submit idea: ${err.message}`);
      },
    });
  };

  // Empties the form but keeps country and department, which describe the author rather than the idea.
  const discardDraft = () => {
    const { countryId, departmentId } = form.getFieldsValue(['countryId', 'departmentId']);
    clearDraft();
    setRestored(null);
    setShowRestoreBanner(false);
    form.resetFields();
    form.setFieldsValue({
      ...EMPTY_FORM,
      frequency: undefined,
      reach: undefined,
      impact: undefined,
      countryId,
      departmentId,
    });
    setFormKey((k) => k + 1); // remount fields that keep their own state, like attachments
  };

  const restoredAgo = restored ? dayjs(restored.updatedAt).fromNow() : '';

  // The dropdowns' options decide the initial values, so wait for them before mounting the form.
  if (categoriesQuery.isPending || countriesQuery.isPending || departmentsQuery.isPending) {
    return <FormPageSkeleton fields={4} />;
  }

  return (
    <PageContainer>
      <Space
        direction="vertical"
        size="large"
        style={{ display: 'flex', animation: 'slide-up var(--dur-slow) var(--ease-out) both' }}
      >
        <Flex align="center" justify="space-between" gap={12} wrap>
          <BackButton fallbackTo="/ideas" />
          {showRestoreBanner ? (
            <Flex align="center" gap={8} className={styles.draftBar}>
              <HistoryOutlined aria-hidden />
              <span>Draft from {restoredAgo} restored</span>
              <Button type="link" size="small" onClick={discardDraft} className={styles.draftDiscard}>
                Discard
              </Button>
            </Flex>
          ) : null}
        </Flex>
        <Card>
          <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
            <PageHeader icon={<BulbOutlined />} title="Submit an idea" />

            <IdeaGuide />

            {categoriesQuery.isError ? (
              <InlineError title="Couldn't load categories" detail={categoriesQuery.error.message} />
            ) : null}
            {countriesQuery.isError || departmentsQuery.isError ? (
              <InlineError
                title="Couldn't load countries and departments"
                detail={(countriesQuery.error ?? departmentsQuery.error)?.message}
              />
            ) : null}

            <Form
              key={formKey}
              form={form}
              layout="vertical"
              className={styles.form}
              initialValues={initialValues}
              onFinish={onFinish}
              disabled={createIdea.isPending || categoriesQuery.isPending}
            >
              <Flex gap={16} wrap>
                <Form.Item
                  name="countryId"
                  label="Country"
                  rules={[{ required: true, message: 'Pick a country' }]}
                  style={{ flex: '1 1 240px', marginBottom: workplaceFilledIn ? 4 : undefined }}
                >
                  <Select
                    options={countryOptions}
                    showSearch
                    optionFilterProp="label"
                    virtual={false}
                    onChange={(countryId: string) => writeWorkplace({ countryId })}
                  />
                </Form.Item>
                <Form.Item
                  name="departmentId"
                  label="Department / Team"
                  rules={[{ required: true, message: 'Pick a department' }]}
                  style={{ flex: '1 1 240px', marginBottom: workplaceFilledIn ? 4 : undefined }}
                >
                  <Select
                    options={departmentOptions}
                    showSearch
                    optionFilterProp="label"
                    virtual={false}
                    onChange={(departmentId: string) => writeWorkplace({ departmentId })}
                  />
                </Form.Item>
              </Flex>
              {workplaceFilledIn ? (
                <Typography.Paragraph type="secondary" style={{ marginBottom: 24 }}>
                  Filled in from your last idea. Change it if it is wrong.
                </Typography.Paragraph>
              ) : null}

              <Form.Item
                name="title"
                label={
                  <span>
                    Idea title{' '}
                    <Typography.Text type="secondary">({IDEA_TITLE_MAX_WORDS} words or fewer)</Typography.Text>
                  </span>
                }
                rules={[
                  englishOnlyRule,
                  { required: true, message: 'Title is required' },
                  { min: 3, max: TITLE_MAX, message: `Title must be 3–${TITLE_MAX} characters` },
                  {
                    validator: (_, value: string | undefined) =>
                      countWords(value ?? '') > IDEA_TITLE_MAX_WORDS
                        ? Promise.reject(new Error(`Keep it to ${IDEA_TITLE_MAX_WORDS} words or fewer`))
                        : Promise.resolve(),
                  },
                ]}
              >
                <Input
                  maxLength={TITLE_MAX}
                  showCount
                  placeholder="e.g. Auto-reply for deposit questions after 10pm"
                  onPaste={plainTypographyOnPaste}
                />
              </Form.Item>

              <Form.Item name="categoryId" label="Category" rules={[{ required: true, message: 'Pick a category' }]}>
                {/* virtual={false}: a handful of options gains nothing from rc-virtual-list, and its height
                    measuring is the likeliest piece to render a blank panel on some setups. */}
                <Select loading={categoriesQuery.isPending} options={categoryOptions} virtual={false} />
              </Form.Item>

              <Form.Item
                name="problem"
                label={IDEA_PROBLEM_LABEL}
                extra={IDEA_PROBLEM_HINT}
                rules={[
                  englishOnlyRule,
                  { required: true, whitespace: true, message: 'Say what happens today' },
                  { min: 10, max: IDEA_PROBLEM_MAX, message: `10–${IDEA_PROBLEM_MAX} characters` },
                ]}
              >
                <Input.TextArea
                  autoSize={{ minRows: 3, maxRows: 10 }}
                  maxLength={IDEA_PROBLEM_MAX}
                  placeholder="e.g. After 10pm, deposit chats wait until 9am for a reply. About 40 chats a night."
                  onPaste={plainTypographyOnPaste}
                />
              </Form.Item>

              <Form.Item
                name="solution"
                label={IDEA_SOLUTION_LABEL}
                extra={IDEA_SOLUTION_HINT}
                rules={[
                  englishOnlyRule,
                  { required: true, whitespace: true, message: 'Suggest a fix' },
                  { min: 5, max: IDEA_SOLUTION_MAX, message: `5–${IDEA_SOLUTION_MAX} characters` },
                ]}
              >
                <Input.TextArea
                  autoSize={{ minRows: 2, maxRows: 8 }}
                  maxLength={IDEA_SOLUTION_MAX}
                  placeholder="e.g. Add an auto-reply from 10pm to 9am with answers to the 3 most asked questions."
                  onPaste={plainTypographyOnPaste}
                />
              </Form.Item>

              <Form.Item
                name="frequency"
                label="Frequency: How often does this happen?"
                rules={[{ required: true, message: 'Pick how often this happens' }]}
              >
                <IdeaScaleField labels={IDEA_FREQUENCY_LABELS} />
              </Form.Item>

              <Form.Item
                name="reach"
                label="Reach: Who does this affect, or who would benefit?"
                rules={[{ required: true, message: 'Pick who this affects' }]}
              >
                <IdeaScaleField labels={IDEA_REACH_LABELS} />
              </Form.Item>

              <Form.Item
                name="impact"
                label="Impact: How much difference will this make?"
                rules={[{ required: true, message: 'Pick how much difference this makes' }]}
              >
                <IdeaScaleField labels={IDEA_IMPACT_LABELS} descriptions={IDEA_IMPACT_DESCRIPTIONS} />
              </Form.Item>

              <Form.Item
                name="attachments"
                label={
                  <span>
                    Attachments <Typography.Text type="secondary">(optional)</Typography.Text>
                  </span>
                }
              >
                <AttachmentsField />
              </Form.Item>

              <IdeaChecklist
                title={title}
                problem={problem}
                solution={solution}
                frequency={frequency}
                reach={reach}
                impact={impact}
              />

              <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
                <Flex justify="flex-end" align="center" gap={8}>
                  <Link to="/ideas">
                    <Button type="text" size="large">
                      Cancel
                    </Button>
                  </Link>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    loading={createIdea.isPending}
                    className={styles.submit}
                  >
                    Submit idea
                  </Button>
                </Flex>
              </Form.Item>
            </Form>
          </Space>
        </Card>
        <LeavePageModal {...leavePageModalProps}>
          You have unsaved changes. Your draft is saved automatically — you can come back and continue where you left
          off.
        </LeavePageModal>
      </Space>
    </PageContainer>
  );
};

export const Route = createFileRoute('/_auth/submit')({
  component: SubmitPage,
});
